import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Wallet as WalletIcon,
  Coins,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Search,
  Inbox,
} from 'lucide-react';
import { PageGuard } from '@components/PageGuard';
import { StatCard } from '@components/dashboard/StatCard';
import {
  ledgerService,
  WALLET_TYPE_CODES,
  HELD_INCOMING_NOTE,
  RAW_AVAILABLE_NOTE,
  type LedgerWallet,
  type WalletOwnerType,
} from '@services/ledger.service';
import { cn, formatCurrency } from '@lib/utils';
import { humanizeCode, ownerTypeLabel, systemWalletBlurb } from './ledgerFormat';

const PAGE_SIZE = 50;

const inputClass =
  'rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors';

const thClass =
  'text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] whitespace-nowrap';

const microLabel =
  'text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]';

/** Incoming earmarks are the normal state of a live order — muted, never danger. */
const incomingClass = 'text-[14px] font-medium text-[var(--text-secondary)]';

/** Stranded holds are the only figure that warrants alarm. */
function strandedClass(value: number): string {
  return value > 0
    ? 'text-[14px] font-semibold text-[var(--state-danger)]'
    : 'text-[14px] font-medium text-[var(--text-muted)]';
}

/** Raw `available` — kept visible for auditability, rendered as small print. */
function rawAvailableLine(value: number, currency?: string): string {
  return `${RAW_AVAILABLE_NOTE}: ${formatCurrency(value, currency)}`;
}

export const WalletsPage = () => {
  const navigate = useNavigate();

  // The wallet type filter lives in the URL so a money-dashboard bucket can
  // link straight to the wallets that make it up
  // (`/finance/wallets?walletTypeCode=branch_float`).
  const [searchParams, setSearchParams] = useSearchParams();
  const walletTypeCode = searchParams.get('walletTypeCode') ?? '';
  const setWalletTypeCode = (code: string) => {
    const next = new URLSearchParams(searchParams);
    if (code) next.set('walletTypeCode', code);
    else next.delete('walletTypeCode');
    setSearchParams(next, { replace: true });
  };

  const [ownerType, setOwnerType] = useState<'' | WalletOwnerType>('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [negativeOnly, setNegativeOnly] = useState(false);
  const [page, setPage] = useState(1);

  const resetTo = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const {
    data: systemWallets,
    isLoading: systemLoading,
    error: systemError,
  } = useQuery({
    queryKey: ['ledger', 'system-wallets'],
    queryFn: () => ledgerService.listSystemWallets(),
  });

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['ledger', 'wallets', ownerType, walletTypeCode, q, negativeOnly, page],
    queryFn: () =>
      ledgerService.listWallets({
        ownerType: ownerType || undefined,
        walletTypeCode: walletTypeCode || undefined,
        q: q || undefined,
        negativeOnly: negativeOnly || undefined,
        page,
        size: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const totals = data?.totals;
  const wallets = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const openWallet = (walletId: string) => navigate(`/finance/wallets/${walletId}`);

  return (
    <PageGuard permission="wallets.read">
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Finance
          </p>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Where is the money
          </h1>
          <p className="text-[14px] text-[var(--text-muted)]">
            Every wallet on the platform. Balance is what is in the wallet now,
            incoming is what escrow has earmarked for it, spendable is what the
            holder can actually use.
          </p>
        </div>

        {/* Platform rollup — reflects the active filters, not just this page */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <StatCard
            label="Total balance"
            value={totals ? formatCurrency(totals.grandTotal.sumBalance) : '—'}
            icon={WalletIcon}
            variant="green"
            footer={`${totals?.grandTotal.count ?? 0} wallets in view`}
          />
          <StatCard
            label="Incoming (earmarked)"
            value={totals ? formatCurrency(totals.grandTotal.sumHeldIncoming) : '—'}
            icon={Inbox}
            variant="purple"
            footer={HELD_INCOMING_NOTE}
          />
          <StatCard
            label="Stranded"
            value={totals ? formatCurrency(totals.grandTotal.sumHeldStranded) : '—'}
            icon={AlertTriangle}
            variant={
              totals && totals.grandTotal.sumHeldStranded > 0 ? 'red' : 'green'
            }
            footer={
              totals && totals.grandTotal.sumHeldStranded > 0
                ? 'Held against baskets that already finished — clear from the treasury adjust-hold worklist'
                : 'No holds sitting on finished baskets'
            }
          />
          <StatCard
            label="Spendable"
            value={totals ? formatCurrency(totals.grandTotal.sumSpendable) : '—'}
            icon={Coins}
            variant="purple"
            footer="Balance minus holds that genuinely lock it — what can move today"
          />
        </div>

        {totals && (
          <p className="text-[12px] text-[var(--text-muted)]">
            {rawAvailableLine(totals.grandTotal.sumAvailable)} · held{' '}
            {formatCurrency(totals.grandTotal.sumHeld)}. A negative raw available
            is what an incoming escrow earmark arithmetically produces — normal
            while orders are in flight, not a fault.
          </p>
        )}

        {/* System wallets — the platform's own money */}
        <section className="space-y-4">
          <div className="space-y-1">
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
              System wallets
            </h2>
            <p className="text-[13px] text-[var(--text-muted)]">
              Chekit's own money. Every inbound payment passes through escrow before
              anything is split.
            </p>
          </div>

          {systemLoading && (
            <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-12 text-center text-[14px] text-[var(--text-muted)]">
              Loading system wallets...
            </div>
          )}

          {!!systemError && !systemLoading && (
            <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-12 text-center text-[14px] text-[var(--state-danger)]">
              Failed to load system wallets.
            </div>
          )}

          {systemWallets && systemWallets.length === 0 && (
            <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-12 text-center text-[14px] text-[var(--text-muted)]">
              No system wallets found.
            </div>
          )}

          {systemWallets && systemWallets.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {systemWallets.map((w: LedgerWallet) => (
                <button
                  key={w.walletId}
                  onClick={() => openWallet(w.walletId)}
                  className={cn(
                    'text-left rounded-3xl bg-[var(--bg-surface)] shadow-soft p-6 transition-all duration-300',
                    'hover:shadow-lift hover:-translate-y-0.5',
                    w.heldStranded > 0 &&
                      'border border-[var(--state-danger-border)]',
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold text-[var(--text-primary)] truncate">
                        {w.typeName || humanizeCode(w.typeCode)}
                      </div>
                      <div className="text-[12px] text-[var(--text-muted)] truncate">
                        {w.typeCode}
                      </div>
                    </div>
                    {w.heldStranded > 0 && (
                      <AlertTriangle
                        className="w-4 h-4 shrink-0 text-[var(--state-danger)]"
                        strokeWidth={1.5}
                      />
                    )}
                  </div>

                  <div className="font-display text-[28px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mt-4 leading-none">
                    {formatCurrency(w.balance, w.currency)}
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-3 pt-4 border-t border-[var(--border-subtle)]">
                    <div>
                      <div className={microLabel}>Incoming</div>
                      <div className={incomingClass}>
                        {formatCurrency(w.heldIncoming, w.currency)}
                      </div>
                    </div>
                    <div>
                      <div className={microLabel}>Stranded</div>
                      <div className={strandedClass(w.heldStranded)}>
                        {formatCurrency(w.heldStranded, w.currency)}
                      </div>
                    </div>
                    <div>
                      <div className={microLabel}>Spendable</div>
                      <div className="text-[14px] font-medium text-[var(--text-primary)]">
                        {formatCurrency(w.spendable, w.currency)}
                      </div>
                    </div>
                  </div>

                  {/* The DB value stays on screen — demoted, never hidden. */}
                  <p className="text-[11px] text-[var(--text-muted)] mt-3">
                    {rawAvailableLine(w.availableBalance, w.currency)} · held{' '}
                    {formatCurrency(w.heldBalance, w.currency)}
                  </p>

                  {systemWalletBlurb[w.typeCode] && (
                    <p className="text-[12px] text-[var(--text-muted)] mt-3">
                      {systemWalletBlurb[w.typeCode]}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Rollup by wallet type */}
        {totals && totals.byWalletType.length > 0 && (
          <section className="space-y-4">
            <div className="space-y-1">
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                Money by wallet type
              </h2>
              <p className="text-[13px] text-[var(--text-muted)]">
                Aggregated over every wallet matching the filters below — not just
                the current page.
              </p>
            </div>

            <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[var(--bg-base)]">
                    <tr>
                      <th className={thClass}>Wallet type</th>
                      <th className={thClass}>Wallets</th>
                      <th className={thClass}>Balance</th>
                      <th className={thClass}>Incoming (earmarked)</th>
                      <th className={thClass}>Stranded</th>
                      <th className={thClass}>Spendable</th>
                      <th className={thClass}>Raw available</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-subtle)]">
                    {totals.byWalletType.map((row) => (
                      <tr key={row.typeCode} className="hover:bg-[var(--bg-base)] transition-colors">
                        <td className="px-6 py-3">
                          <div className="text-[14px] font-medium text-[var(--text-primary)]">
                            {row.typeName || humanizeCode(row.typeCode)}
                          </div>
                          <div className="text-[12px] text-[var(--text-muted)]">{row.typeCode}</div>
                        </td>
                        <td className="px-6 py-3 text-[14px] text-[var(--text-muted)]">{row.count}</td>
                        <td className="px-6 py-3 text-[14px] text-[var(--text-primary)]">
                          {formatCurrency(row.sumBalance)}
                        </td>
                        <td className={cn('px-6 py-3', incomingClass)}>
                          {formatCurrency(row.sumHeldIncoming)}
                        </td>
                        <td className={cn('px-6 py-3', strandedClass(row.sumHeldStranded))}>
                          {formatCurrency(row.sumHeldStranded)}
                        </td>
                        <td className="px-6 py-3 text-[14px] font-medium text-[var(--text-primary)]">
                          {formatCurrency(row.sumSpendable)}
                        </td>
                        <td className="px-6 py-3 text-[12px] text-[var(--text-muted)]">
                          {formatCurrency(row.sumAvailable)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* All wallets */}
        <section className="space-y-4">
          <div className="space-y-1">
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
              All wallets
            </h2>
            <p className="text-[13px] text-[var(--text-muted)]">
              Open any wallet for its statement and running balance.
            </p>
          </div>

          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={ownerType}
              onChange={(e) =>
                resetTo(() => setOwnerType(e.target.value as '' | WalletOwnerType))
              }
              className={cn(inputClass, 'w-48')}
            >
              <option value="">All owners</option>
              <option value="system">Chekit platform</option>
              <option value="business">Business</option>
              <option value="branch">Branch</option>
              <option value="user">User</option>
            </select>

            <select
              value={walletTypeCode}
              onChange={(e) => resetTo(() => setWalletTypeCode(e.target.value))}
              className={cn(inputClass, 'w-56')}
            >
              <option value="">All wallet types</option>
              {WALLET_TYPE_CODES.map((code) => (
                <option key={code} value={code}>
                  {humanizeCode(code)}
                </option>
              ))}
            </select>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                resetTo(() => setQ(search.trim()));
              }}
              className="relative"
            >
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
                strokeWidth={1.5}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search owner name..."
                className={cn(inputClass, 'w-64 pl-10')}
              />
            </form>

            {/* This filters on the RAW generated column, which is negative for
                ordinary in-flight escrow too — so it is labelled as such rather
                than implying every hit is a fault. */}
            <button
              type="button"
              onClick={() => resetTo(() => setNegativeOnly((v) => !v))}
              title="Filters on the raw generated column (balance − held). Negative is normal while orders are in flight."
              className={cn(
                'inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-[14px] font-medium transition-colors border',
                negativeOnly
                  ? 'border-[var(--border-strong)] bg-[var(--bg-elevated)] text-[var(--text-primary)]'
                  : 'border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-muted)] hover:text-[var(--text-primary)]',
              )}
            >
              <Coins className="w-4 h-4" strokeWidth={1.5} />
              Raw available below zero
            </button>

            {isFetching && !isLoading && (
              <span className="text-[13px] text-[var(--text-muted)]">Updating...</span>
            )}
          </div>

          {/* Table card */}
          <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft overflow-hidden">
            {isLoading && (
              <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
                Loading wallets...
              </div>
            )}

            {!!error && !isLoading && (
              <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
                Failed to load wallets.
              </div>
            )}

            {data && !isLoading && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[var(--bg-base)]">
                    <tr>
                      <th className={thClass}>Wallet</th>
                      <th className={thClass}>Owner</th>
                      <th className={thClass}>Balance</th>
                      <th className={thClass}>Incoming (earmarked)</th>
                      <th className={thClass}>Stranded</th>
                      <th className={thClass}>Spendable</th>
                      <th className={thClass}>Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-subtle)]">
                    {wallets.map((w) => (
                      <tr
                        key={w.walletId}
                        onClick={() => openWallet(w.walletId)}
                        className="hover:bg-[var(--bg-base)] transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-4">
                          <div className="text-[14px] font-medium text-[var(--text-primary)]">
                            {w.typeName || humanizeCode(w.typeCode)}
                          </div>
                          <div className="text-[12px] text-[var(--text-muted)]">{w.typeCode}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-[14px] text-[var(--text-primary)] truncate max-w-[240px]">
                            {w.ownerLabel ?? '—'}
                          </div>
                          <div className="text-[12px] text-[var(--text-muted)]">
                            {ownerTypeLabel[w.ownerType] ?? w.ownerType}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-[14px] text-[var(--text-primary)]">
                            {formatCurrency(w.balance, w.currency)}
                          </div>
                          {/* DB value stays auditable, in small print. */}
                          <div className="text-[11px] text-[var(--text-muted)]">
                            {rawAvailableLine(w.availableBalance, w.currency)}
                          </div>
                        </td>
                        <td className={cn('px-6 py-4 whitespace-nowrap', incomingClass)}>
                          {formatCurrency(w.heldIncoming, w.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5',
                              strandedClass(w.heldStranded),
                            )}
                          >
                            {w.heldStranded > 0 && (
                              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} />
                            )}
                            {formatCurrency(w.heldStranded, w.currency)}
                          </span>
                          {w.heldStranded > 0 && (
                            <div className="text-[12px] font-semibold text-[var(--state-danger)]">
                              Clear hold →
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-[14px] font-medium text-[var(--text-primary)] whitespace-nowrap">
                          {formatCurrency(w.spendable, w.currency)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={cn(
                              'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                              w.status === 'active'
                                ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                                : 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                            )}
                          >
                            {w.status}
                          </span>
                        </td>
                      </tr>
                    ))}

                    {wallets.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]"
                        >
                          No wallets match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {data && totalCount > 0 && (
              <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-[var(--border-subtle)]">
                <span className="text-[13px] text-[var(--text-muted)]">
                  Page {page} of {lastPage} · {totalCount} wallets
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="inline-flex items-center gap-1 rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed hover:border-[var(--border-strong)] transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                    disabled={page >= lastPage}
                    className="inline-flex items-center gap-1 rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed hover:border-[var(--border-strong)] transition-colors"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </PageGuard>
  );
};
